import React, { useState } from 'react';
import { Card, Form, Divider, Button, Typography, Spin, Input, Row, Col } from 'antd';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'dva';
import styles from './../styles.less';
import { ButtonBar, CurrentPasswordModal } from '../../index';

const passwordRequestTypes = {
  enable: 'ENABLE',
  regenerateCodes: 'REGENERATE_CODES',
  delete: 'DELETE',
};

const TwoFACard = ({ dispatch, loading, intl, form, app }) => {
  const [enablingData, setEnablingData] = useState({ enabled: false, qrCode: null, secretKey: null });
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [openCurrentPasswordModalData, setOpenCurrentPasswordModalData] = useState(null);
  const isUserEnabled = !!app.user.two_factor_confirmed_at;
  const isEnablingDisabled = loading.effects['users/userEnableTwoFactorAuth'];

  const send2FARequest = (type, currentPassword) => {
    if (type === passwordRequestTypes.enable) {
      handleEnable2FA(currentPassword);
    } else if (type === passwordRequestTypes.delete) {
      delete2FA(currentPassword);
    } else if (type === passwordRequestTypes.regenerateCodes) {
      regenerateRecoveryCodes(currentPassword);
    }
  };

  const submitCode = (e) => {
    e.preventDefault();

    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        dispatch({
          type: 'users/enterTwoFactorCode',
          payload: {
            code: values.code,
          },
        }).then((result) => {
          setRecoveryCodes(result.body);
          if (result.success) {
            dispatch({
              type: 'app/start',
            });
          }
        });
      }
    });
  };

  const regenerateRecoveryCodes = (currentPassword) => {
    dispatch({
      type: 'users/userRegenerateTwoFactorRecoveryCodes',
      payload: {
        current_password: currentPassword,
      },
    }).then((result) => {
      setRecoveryCodes(result.body);
      setOpenCurrentPasswordModalData(null);
    });
  };

  const handleEnable2FA = (currentPassword) => {
    dispatch({
      type: 'users/userEnableTwoFactorAuth',
      payload: {
        current_password: currentPassword,
      },
    }).then((result) => {
      if (!result.success) {
        return;
      }
      setOpenCurrentPasswordModalData(null);
      setEnablingData(prevState => ({ ...prevState, enabled: true }));
      dispatch({
        type: 'users/userGetTwoFactorQRCode',
      }).then((qrCodeResult) => {
        setEnablingData(prevState => ({ ...prevState, qrCode: qrCodeResult.body.svg }));
      });
      dispatch({
        type: 'users/userGetTwoFactorSecretKey',
      }).then((secretKeyResult) => {
        setEnablingData(prevState => ({ ...prevState, secretKey: secretKeyResult.body.secretKey }));
      });
    });
  };

  const delete2FA = (currentPassword) => {
    dispatch({
      type: 'users/userDeleteTwoFactor',
      payload: {
        current_password: currentPassword,
      },
    }).then((result) => {
      if (result.success) {
        setOpenCurrentPasswordModalData(null);
        setEnablingData({ enabled: false, qrCode: null, secretKey: null, recoveryCodes: null });
        dispatch({
          type: 'app/start',
        });
      }
    });
  };

  return (
    <Card className={styles.noMarginTop}>
      <h3><FormattedMessage id="Component.2FA.Title" /></h3>
      <Typography.Paragraph><FormattedMessage id="Component.2FA.Description" /></Typography.Paragraph>
      <Divider />
      <h4><FormattedMessage id={`Component.2FA.${isUserEnabled ? '' : 'Not'}EnabledText`} /></h4>
      <Typography.Paragraph><FormattedMessage id="Component.2FA.GoalDescription" /></Typography.Paragraph>

      {!isUserEnabled ? (
        <React.Fragment>
          {!enablingData.enabled ? <Button onClick={() => setOpenCurrentPasswordModalData(passwordRequestTypes.enable)} type="primary" className={styles.btnSubmit} disabled={isEnablingDisabled}>
            <FormattedMessage id="Component.2FA.Enable" />
          </Button> : (
            <Spin
              spinning={
                !!loading.effects['users/userGetTwoFactorQRCode'] ||
                !!loading.effects['users/userGetTwoFactorSecretKey'] ||
                !!loading.effects['users/userEnableTwoFactorAuth'] ||
                !!loading.effects['users/userDeleteTwoFactor']
              }
            >
              <Typography.Paragraph strong><FormattedMessage id="Component.2FA.FinishEnablingText" /></Typography.Paragraph>
              {/* eslint-disable-next-line react/no-danger */}
              {enablingData.qrCode && <div className={styles.qrCodeDiv} dangerouslySetInnerHTML={{ __html: enablingData.qrCode }} />}
              {enablingData.secretKey && <Typography.Paragraph strong>
                <FormattedMessage id="Component.2FA.SetupKey" values={{ key: enablingData.secretKey }} />
              </Typography.Paragraph>}

              <Form onSubmit={submitCode}>
                <Row type="flex" gutter={32}>
                  <Col span={12}>
                    <Form.Item label={<FormattedMessage id="Component.2FA.CodeLabel" />}>
                      {form.getFieldDecorator('code', {
                        rules: [{
                          required: true,
                          min: 1,
                          max: 255,
                          message: intl.formatMessage({ id: 'Common.Field.Required' }),
                        }],
                      })(
                        <Input maxLength={255} />,
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <ButtonBar cancelAction={() => setEnablingData({ enabled: false, qrCode: null, secretKey: null, recoveryCodes: null })} />
              </Form>
            </Spin>
          )}
        </React.Fragment>
      ) : (
        <Spin
          spinning={
            !!loading.effects['users/userRegenerateTwoFactorRecoveryCodes'] ||
            !!loading.effects['users/userDeleteTwoFactor']
          }
        >
          {!!recoveryCodes.length && (
            <React.Fragment>
              <Typography.Paragraph strong><FormattedMessage id="Component.2FA.RecoveryCodesDescription" /></Typography.Paragraph>
              <ul>
                {recoveryCodes.map((code, index) => <li key={index}>{code}</li>)}
              </ul>
            </React.Fragment>
          )}
          <Row>
            <Col>
              <Button type="primary" onClick={() => setOpenCurrentPasswordModalData(passwordRequestTypes.regenerateCodes)}>
                <FormattedMessage id={'Component.2FA.RegenerateRecoveryCodes'} />
              </Button>
              <Button type="danger" onClick={() => setOpenCurrentPasswordModalData(passwordRequestTypes.delete)} className={styles.btnRemoveRecoveryCode}>
                <FormattedMessage id="Component.2FA.Disable" />
              </Button>
            </Col>
          </Row>
        </Spin>
      )}
      {!!openCurrentPasswordModalData && (
        <CurrentPasswordModal
          visible={!!openCurrentPasswordModalData}
          onCancel={() => setOpenCurrentPasswordModalData(null)}
          onSuccess={currentPassword => send2FARequest(openCurrentPasswordModalData, currentPassword)}
        />)}
    </Card>
  );
};

export default connect(
  ({ app, users, loading }) => ({ app, users, loading }))(Form.create()(injectIntl(TwoFACard)));



// WEBPACK FOOTER //
// ./src/components/UsersAdministration/TwoFACard/TwoFACard.js